import { useRouter } from 'expo-router';
import { ArrowLeft } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { PremiumFooter } from '../src/components/landing/PremiumFooter';
import { Container } from '../src/components/primitives/Container';
import { Text } from '../src/components/primitives/Text';
import { useTranslation } from '../src/contexts/TranslationContext';
import { colors } from '../src/theme/colors';
import { spacing } from '../src/theme/spacing';

const LAST_UPDATED = 'July 27, 2026';

interface SectionProps {
  title: string;
  children: ReactNode;
}

const Section = ({ title, children }: SectionProps) => (
  <View style={styles.section}>
    <Text variant="h4" style={styles.sectionTitle}>
      {title}
    </Text>
    {children}
  </View>
);

const Paragraph = ({ children }: { children: string }) => (
  <Text variant="body" color={colors.text.secondary} style={styles.paragraph}>
    {children}
  </Text>
);

const BulletItem = ({ children }: { children: string }) => (
  <View style={styles.bulletRow}>
    <Text variant="body" color={colors.primary[500]} style={styles.bullet}>
      {'\u2022'}
    </Text>
    <Text variant="body" color={colors.text.secondary} style={styles.bulletText}>
      {children}
    </Text>
  </View>
);

export default function PrivacyPolicyPage() {
  const router = useRouter();
  const { t } = useTranslation();

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Container maxWidth="md">
        <Pressable
          onPress={() => router.push('/landing')}
          style={styles.backButton}
          accessibilityRole="button"
          accessibilityLabel={t('common.actions.goBack')}
        >
          <ArrowLeft size={20} color={colors.primary[500]} />
          <Text variant="bodySmall" color={colors.primary[500]}>
            {t('common.actions.goBack')}
          </Text>
        </Pressable>

        <View style={styles.header}>
          <Text variant="h1" style={styles.title}>
            Privacy Policy
          </Text>
          <Text variant="bodySmall" color={colors.text.tertiary}>
            Last updated: {LAST_UPDATED}
          </Text>
        </View>

        <View style={styles.divider} />

        <Section title="1. Introduction">
          <Paragraph>
            Welcome to Funnel Panel ("we," "our," or "us"). We are committed to protecting your
            privacy and ensuring the security of your personal information. This Privacy Policy
            explains how we collect, use, disclose, and safeguard your information when you use our
            sales funnel tracking and analytics platform, including our website, mobile
            applications, and related services (collectively, the "Service").
          </Paragraph>
          <Paragraph>
            By accessing or using the Service, you agree to the collection and use of information in
            accordance with this Privacy Policy. If you do not agree with the terms of this Privacy
            Policy, please do not access or use the Service.
          </Paragraph>
        </Section>

        <Section title="2. Information We Collect">
          <Text variant="body" color={colors.text.primary} style={styles.subsectionTitle}>
            2.1 Information You Provide
          </Text>
          <BulletItem>
            Account information: name, email address, company name, phone number, and password when
            you create an account or join our waitlist.
          </BulletItem>
          <BulletItem>
            Organization data: team details, member roles, and organizational settings you configure
            within the Service.
          </BulletItem>
          <BulletItem>
            Lead and contact data: information about your sales leads that you input or import into
            the platform, including names, emails, phone numbers, and custom fields.
          </BulletItem>
          <BulletItem>
            Payment records: Wompi receives the contact and transaction details needed to process an
            authorized purchase. We retain billing metadata for accounting, support, refunds, and
            legal compliance, but do not store full card numbers on our servers.
          </BulletItem>
          <BulletItem>
            Communications: messages, feedback, and support requests you send to us.
          </BulletItem>

          <Text variant="body" color={colors.text.primary} style={styles.subsectionTitle}>
            2.2 Information Collected Automatically
          </Text>
          <BulletItem>
            Usage data: pages visited, features used, actions taken, time spent, and interaction
            patterns within the Service.
          </BulletItem>
          <BulletItem>
            Device information: device type, operating system, browser type, screen resolution, and
            unique device identifiers.
          </BulletItem>
          <BulletItem>
            Log data: IP address, access times, referring URLs, and error logs.
          </BulletItem>
          <BulletItem>
            Analytics data: we use Mixpanel and similar tools to collect anonymized usage analytics
            to improve the Service.
          </BulletItem>
        </Section>

        <Section title="3. How We Use Your Information">
          <Paragraph>We use the information we collect to:</Paragraph>
          <BulletItem>Provide, maintain, and improve the Service.</BulletItem>
          <BulletItem>Process your account registration and manage your subscription.</BulletItem>
          <BulletItem>
            Send you transactional emails, including account confirmations, invoices, technical
            notices, and support responses.
          </BulletItem>
          <BulletItem>
            Send marketing communications (with your consent) about new features, promotions, and
            updates. You can opt out at any time.
          </BulletItem>
          <BulletItem>
            Analyze usage trends and optimize the user experience across platforms (iOS, Android,
            Web).
          </BulletItem>
          <BulletItem>
            Detect, prevent, and address fraud, security issues, and technical problems.
          </BulletItem>
          <BulletItem>Comply with legal obligations and enforce our terms of service.</BulletItem>
        </Section>

        <Section title="4. Data Sharing and Disclosure">
          <Paragraph>
            We do not sell your personal information. We may share your data with:
          </Paragraph>
          <BulletItem>
            Service providers: third-party vendors who assist us in operating the Service, such as
            cloud hosting (AWS), authentication (Auth0), email delivery, analytics (Mixpanel), and
            payment processing (Wompi) and legacy payment records.
          </BulletItem>
          <BulletItem>
            Within your organization: other members of your organization on Funnel Panel can access
            shared data such as leads, funnel stages, and analytics as configured by your
            organization's administrators.
          </BulletItem>
          <BulletItem>
            Legal requirements: if required by law, regulation, legal process, or governmental
            request, we may disclose your information.
          </BulletItem>
          <BulletItem>
            Business transfers: in the event of a merger, acquisition, or sale of assets, your
            information may be transferred as part of that transaction.
          </BulletItem>
        </Section>

        <Section title="5. Data Security">
          <Paragraph>
            We implement industry-standard security measures to protect your information, including:
          </Paragraph>
          <BulletItem>Encryption of data in transit (TLS/SSL) and at rest.</BulletItem>
          <BulletItem>Secure authentication via Auth0 with JWT-based access tokens.</BulletItem>
          <BulletItem>Role-based access controls within organizations.</BulletItem>
          <BulletItem>Regular security audits and vulnerability assessments.</BulletItem>
          <BulletItem>Rate limiting and CORS protections on our API endpoints.</BulletItem>
          <Paragraph>
            While we strive to use commercially acceptable means to protect your personal data, no
            method of transmission over the Internet or electronic storage is 100% secure.
          </Paragraph>
        </Section>

        <Section title="6. Data Retention">
          <Paragraph>
            We retain your personal information for as long as your account is active or as needed
            to provide the Service. If you delete your account, we will delete or anonymize your
            personal data within 30 days, except where retention is required by law or for
            legitimate business purposes such as resolving disputes or enforcing our agreements.
          </Paragraph>
          <Paragraph>
            Lead data and funnel analytics associated with your organization are retained for the
            duration of your subscription and for up to 90 days after account closure to allow for
            data export.
          </Paragraph>
        </Section>

        <Section title="7. Your Rights">
          <Paragraph>Depending on your location, you may have the right to:</Paragraph>
          <BulletItem>Access the personal data we hold about you.</BulletItem>
          <BulletItem>Correct inaccurate or incomplete data.</BulletItem>
          <BulletItem>Delete your personal data (subject to legal obligations).</BulletItem>
          <BulletItem>Object to or restrict certain processing of your data.</BulletItem>
          <BulletItem>
            Data portability: receive your data in a structured, machine-readable format.
          </BulletItem>
          <BulletItem>Withdraw consent for marketing communications at any time.</BulletItem>
          <Paragraph>
            To exercise any of these rights, contact us at privacy@flowsoftdev.com.
          </Paragraph>
        </Section>

        <Section title="8. International Data Transfers">
          <Paragraph>
            Your information may be transferred to and processed in countries other than your
            country of residence. We ensure appropriate safeguards are in place, including standard
            contractual clauses and compliance with applicable data protection frameworks.
          </Paragraph>
        </Section>

        <Section title="9. Cookies and Tracking">
          <Paragraph>
            We use cookies and similar tracking technologies to enhance your experience. These
            include:
          </Paragraph>
          <BulletItem>
            Essential cookies: required for the Service to function (authentication, session
            management).
          </BulletItem>
          <BulletItem>
            Analytics cookies: help us understand how users interact with the Service.
          </BulletItem>
          <BulletItem>
            Preference cookies: remember your settings and language preferences.
          </BulletItem>
          <Paragraph>
            You can manage cookie preferences through your browser settings. Disabling certain
            cookies may affect the functionality of the Service.
          </Paragraph>
        </Section>

        <Section title="10. Third-Party Services">
          <Paragraph>
            The Service integrates with third-party services that have their own privacy policies.
            We encourage you to review the privacy policies of these services:
          </Paragraph>
          <BulletItem>Auth0 (authentication)</BulletItem>
          <BulletItem>Mixpanel (analytics)</BulletItem>
          <BulletItem>Amazon Web Services (cloud infrastructure)</BulletItem>
          <Paragraph>
            To show prices in your local currency, we determine your country from your IP address
            using a local database. Your IP address is not sent to any third party for this, and we
            do not store it for this purpose. You can override the detected region at any time using
            the billing region selector on our pricing page.
          </Paragraph>
          <Paragraph>IP address data is powered by IPinfo (https://ipinfo.io).</Paragraph>
        </Section>

        <Section title="11. Children's Privacy">
          <Paragraph>
            The Service is not intended for individuals under the age of 16. We do not knowingly
            collect personal information from children. If we become aware that we have collected
            data from a child without parental consent, we will take steps to delete that
            information promptly.
          </Paragraph>
        </Section>

        <Section title="12. Changes to This Policy">
          <Paragraph>
            We may update this Privacy Policy from time to time. We will notify you of any material
            changes by posting the new policy on this page and updating the "Last updated" date.
            Your continued use of the Service after changes are posted constitutes your acceptance
            of the revised policy.
          </Paragraph>
        </Section>

        <Section title="13. Contact Us">
          <Paragraph>
            If you have any questions about this Privacy Policy or our data practices, please
            contact us at:
          </Paragraph>
          <BulletItem>Email: privacy@flowsoftdev.com</BulletItem>
          <BulletItem>Company: FlowSoft Dev</BulletItem>
        </Section>
      </Container>

      <PremiumFooter />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    flexGrow: 1,
    paddingTop: Platform.select({ web: 60, default: 40 }),
    paddingBottom: spacing.xxl,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xl,
    alignSelf: 'flex-start',
  },
  header: {
    marginBottom: spacing.lg,
    gap: spacing.sm,
  },
  title: {
    fontSize: Platform.select({ web: 42, default: 32 }),
    fontWeight: '700',
    letterSpacing: -0.5,
  },
  divider: {
    height: 1,
    backgroundColor: colors.border.muted,
    marginBottom: spacing.xxl,
  },
  section: {
    marginBottom: spacing.xxl,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: spacing.md,
    color: colors.text.primary,
  },
  subsectionTitle: {
    fontWeight: '600',
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  paragraph: {
    lineHeight: Platform.select({ web: 26, default: 24 }),
    marginBottom: spacing.md,
  },
  bulletRow: {
    flexDirection: 'row',
    paddingLeft: spacing.md,
    marginBottom: spacing.sm,
    gap: spacing.sm,
  },
  bullet: {
    lineHeight: Platform.select({ web: 26, default: 24 }),
  },
  bulletText: {
    flex: 1,
    lineHeight: Platform.select({ web: 26, default: 24 }),
  },
});
